import { Signal, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { ScreenRect } from '../domain/types';

const MIN_DRAFT_PX = 8;

export interface DrawCompletedEvent {
  rect: ScreenRect;
  clientX: number;
  clientY: number;
}

export interface TextPointPickedEvent {
  x: number;
  y: number;
  clientX: number;
  clientY: number;
}

export class DrawController {
  private host: HTMLElement | null = null;
  private mode: 'box' | 'text' | null = null;
  private dragStart: { x: number; y: number } | null = null;
  private readonly _current = signal<ScreenRect | null>(null);
  readonly currentDrag: Signal<ScreenRect | null> = this._current.asReadonly();
  readonly drawCompleted$ = new Subject<DrawCompletedEvent>();
  readonly textPointPicked$ = new Subject<TextPointPickedEvent>();

  private onDown = (e: PointerEvent): void => {
    if (!this.host || this.mode === null) return;
    e.preventDefault();
    e.stopPropagation();
    const { x, y } = this.localXY(e);
    if (this.mode === 'text') {
      this.textPointPicked$.next({ x, y, clientX: e.clientX, clientY: e.clientY });
      return;
    }
    this.dragStart = { x, y };
    this._current.set({ x, y, width: 0, height: 0 });
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.dragStart) return;
    e.preventDefault();
    const { x, y } = this.localXY(e);
    const left = Math.min(this.dragStart.x, x);
    const top = Math.min(this.dragStart.y, y);
    const w = Math.abs(x - this.dragStart.x);
    const h = Math.abs(y - this.dragStart.y);
    this._current.set({ x: left, y: top, width: w, height: h });
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.dragStart) return;
    e.preventDefault();
    const start = this.dragStart;
    const { x, y } = this.localXY(e);
    const rect: ScreenRect = {
      x: Math.min(start.x, x),
      y: Math.min(start.y, y),
      width: Math.abs(x - start.x),
      height: Math.abs(y - start.y),
    };
    this.dragStart = null;
    this._current.set(null);
    if (rect.width < MIN_DRAFT_PX || rect.height < MIN_DRAFT_PX) return;
    this.drawCompleted$.next({ rect, clientX: e.clientX, clientY: e.clientY });
  };

  attach(host: HTMLElement): void {
    this.host = host;
    host.addEventListener('pointerdown', this.onDown);
    host.addEventListener('pointermove', this.onMove);
    host.addEventListener('pointerup', this.onUp);
  }

  detach(): void {
    if (!this.host) return;
    this.host.removeEventListener('pointerdown', this.onDown);
    this.host.removeEventListener('pointermove', this.onMove);
    this.host.removeEventListener('pointerup', this.onUp);
    this.host = null;
  }

  setMode(mode: 'box' | 'text' | null): void {
    this.mode = mode;
  }

  private localXY(e: PointerEvent): { x: number; y: number } {
    const bb = this.host!.getBoundingClientRect();
    return { x: e.clientX - bb.left, y: e.clientY - bb.top };
  }
}
